/**
 * @fileoverview AR-670 — in-memory mock adapter for the Classroom ports,
 * following the READ-ONLY backend contract (branch
 * feat-classroom-link-only-courses). Kept for Jest and for developing the
 * screens without a backend; the runtime provider returns the HTTP adapter.
 *
 * Scenario switch and session state live in ./mockState.ts; fixtures in
 * ./mockData.ts.
 */

import { ClassroomAdapters } from '../ports'
import { ClassroomPortError, GradePushResult } from '../types'
import {
  MOCK_COURSES,
  MOCK_LINKED_COURSES,
  MOCK_STUDENT_FILES,
} from './mockData'
import {
  baseProjectLink,
  clearProjectLink,
  CONNECTED_SCHOOL,
  currentConnection,
  delay,
  linkedProjectState,
  readProjectLink,
  rosterForScenario,
  scenario,
  setConnectionOverride,
  toLinkResult,
  writeProjectLink,
} from './mockState'

export { CLASSROOM_MOCK_SCENARIO_KEY } from './mockState'

export const mockClassroomAdapters: ClassroomAdapters = {
  connection: {
    async getConnection() {
      await delay()
      return currentConnection()
    },
    // The mock skips the Google round-trip: connecting is immediate and
    // completeConnect just reports the connected state.
    async startConnect() {
      await delay()
      setConnectionOverride({ ...CONNECTED_SCHOOL, connectedAt: new Date().toISOString() })
    },
    async completeConnect() {
      await delay()
      const connected = { ...CONNECTED_SCHOOL, connectedAt: new Date().toISOString() }
      setConnectionOverride(connected)
      return connected
    },
    async disconnect() {
      await delay()
      setConnectionOverride({ connected: false, status: 'disconnected' })
    },
  },

  courses: {
    // Local synced catalog: needs_reconnection serves the cache (200) and
    // only flags it via connectionStatus.
    async listCourses() {
      await delay()
      const status = currentConnection().status
      return {
        courses: scenario() === 'empty' ? [] : MOCK_COURSES,
        syncedAt: '2026-08-27T04:00:00Z',
        connectionStatus: status === 'needs_reconnection' ? 'needs_reconnection' : 'connected',
      }
    },

    async listLinkedCourses() {
      await delay()
      return scenario() === 'empty' ? [] : MOCK_LINKED_COURSES
    },
  },

  projectLink: {
    async getLink(projectId) {
      await delay()
      const stored = readProjectLink(projectId)
      if (!stored) return baseProjectLink()
      // Connection status always reflects the current scenario.
      return { ...stored, connectionStatus: currentConnection().status }
    },
    async linkCourse(projectId, courseId) {
      await delay()
      const current = scenario()
      if (current === 'already_linked') {
        throw new ClassroomPortError(
          'already_linked',
          'Project is already linked to a Google Classroom course',
        )
      }
      // 1 course = 1 project — the new 422 course_id.
      if (current === 'course_already_linked') {
        throw new ClassroomPortError(
          'course_already_linked',
          'Course is already linked to another project',
        )
      }
      const course = MOCK_COURSES.find(option => option.id === courseId)
      const courseName = course ? course.name : 'Unknown course'
      // Matching failed: the link is KEPT, roster comes null with the error.
      if (current === 'link_roster_error') {
        const state = {
          ...linkedProjectState(courseId, courseName),
          mappedStudentCount: 0,
          rosterStudents: [],
          unmatchedStudents: [],
          lastRosterSyncAt: null,
        }
        writeProjectLink(projectId, state)
        return toLinkResult(state, null, 'Google Classroom API error 503')
      }
      const state = linkedProjectState(courseId, courseName)
      writeProjectLink(projectId, state)
      return toLinkResult(state, rosterForScenario())
    },
    async syncRoster(projectId) {
      await delay()
      const stored = readProjectLink(projectId)
      if (!stored?.linked) {
        throw new ClassroomPortError('not_linked', 'Project is not linked to a Google Classroom course')
      }
      if (!stored.syncEnabled) {
        throw new ClassroomPortError('sync_disabled', 'Google Classroom sync is disabled for this project')
      }
      const summary = rosterForScenario()
      const state = {
        ...stored,
        mappedStudentCount: summary.mappedStudentsCount,
        lastRosterSyncAt: new Date().toISOString(),
        rosterStudents: summary.matchedCount > 0 ? stored.rosterStudents : [],
        unmatchedStudents: summary.unmatched,
      }
      writeProjectLink(projectId, state)
      return toLinkResult(state, summary)
    },
    async setSyncEnabled(projectId, enabled) {
      await delay()
      const state = { ...(readProjectLink(projectId) || baseProjectLink()), syncEnabled: enabled }
      writeProjectLink(projectId, state)
      return state
    },
    async unlink(projectId) {
      await delay()
      clearProjectLink(projectId)
    },
  },

  submissionSync: {
    async syncSubmissions() {
      await delay()
      const summary = rosterForScenario()
      const imported = summary.mappedStudentsCount > 0 ? 1 : 0
      return [
        {
          studentTaskId: 'task-1',
          title: `${MOCK_COURSES[0].name} - Final essay`,
          summary: {
            imported,
            updated: 0,
            unchanged: 0,
            skippedNotSubmitted: 2,
            skippedUnmapped: [],
            attachmentsSkipped: 0,
            filesDownloaded: imported,
          },
        },
      ]
    },

    async getStudentFiles() {
      await delay()
      const current = scenario()
      if (current === 'disconnected') return { available: false, reason: 'school_not_connected' as const, files: [] }
      // grade_student_not_linked doubles as the "student without a Classroom
      // account" scenario.
      if (current === 'grade_student_not_linked') {
        return { available: false, reason: 'student_not_mapped' as const, files: [] }
      }
      if (current === 'missing_announcements_scope') {
        return { available: false, reason: 'missing_announcements_scope' as const, files: [] }
      }
      if (current === 'empty') return { available: true, files: [] }
      return { available: true, files: MOCK_STUDENT_FILES }
    },
  },

  grade: {
    async pushGrade(_activityId, _studentId, grade): Promise<GradePushResult> {
      await delay()
      switch (scenario()) {
        case 'grade_student_not_linked':
          throw new ClassroomPortError('student_not_linked', 'This student is not linked to Classroom')
        case 'grade_no_submission':
          throw new ClassroomPortError('no_submission', 'No submission in Classroom for this student')
        case 'grade_not_assignment_owner':
          throw new ClassroomPortError('not_assignment_owner', 'You can only grade assignments you own')
        case 'grade_needs_reconnection':
          throw new ClassroomPortError('needs_reconnection', 'The Google Workspace connection expired')
        default:
          return { gcGrade: grade, pushed: true, draft: true }
      }
    },
  },
}
